<template>
    <ds-inline-alert
        v-if="isOverWarningLimit && !closed"
        :type="alertType"
        data-qa="contact-limit-alert"
        @close="closeComponent"
    >
        <template #default>
            <span v-if="isOverLimit" class="alert-title">
                {{ $options.MESSAGE_CONSTANTS.exceededTitle }}
            </span>

            <span v-else class="alert-title">
                {{ $options.MESSAGE_CONSTANTS.defaultTitle }}
            </span>

            <p v-if="isOverLimit" class="usage-message">
                {{ $options.MESSAGE_CONSTANTS.exceededMessage }}
            </p>

            <p v-else class="usage-message">
                {{ $options.MESSAGE_CONSTANTS.defaultMessage }}
            </p>

            <p v-if="!hasManageBillingPermission">
                {{ $options.MESSAGE_CONSTANTS.noBillingPermissionsMessage }}
            </p>

            <ds-progress-bar
                v-if="!isMyAccountPage"
                class="progress-bar"
                :progress="contactUsed"
                :max="contactLimit"
                :label="`${contactUsed} of ${contactLimit} contacts used`"
                hide-label-value
            />
        </template>

        <template #footer>
            <div class="manage-account">
                <ds-outline-button
                    v-if="hasManageBillingPermission"
                    data-qa="quick-add-contacts-button"
                    gray
                    @click="openQuickAddModal"
                >
                    Add contacts
                </ds-outline-button>

                <ds-text-button
                    data-qa="learn-more-button"
                    @click="learnMore"
                >
                    Learn more
                </ds-text-button>
            </div>
        </template>
    </ds-inline-alert>
</template>
<script>
import axios from 'axios';
import amplitude from '@/components/util/analytics/amplitude';

export const CONTACT_USAGE_ALERT_LOCATION = {
    UNKNOWN: 'UNKNOWN',
    MY_ACCOUNT: 'MY_ACCOUNT',
    ADD_CONTACT: 'ADD_CONTACT',
    IMPORT_CONTACTS: 'IMPORT_CONTACTS',
};

export const MESSAGE_CONSTANTS = {
    defaultTitle: 'Looks like you\'re growing',
    exceededTitle: 'You\'ve hit a limit on contacts',
    defaultMessage: 'You\'re approaching the contacts limit for your Keap plan. We recommend cleaning up your contacts or upgrading the limit on your plan.',
    exceededMessage: 'We\'ll automatically increase your contacts limit. These changes will be made to your subscription by your next bill.',
    noBillingPermissionsMessage: 'You do not have permission to manage the contact plan on this account. Please reach out to you account admin to make any changes.',
};

export const BUFFER_TYPE = {
    STATIC: 'static',
    PERCENTAGE: 'percentage',
};

export default {
    MESSAGE_CONSTANTS,

    props: {
        // contactsUsageAlert.jsp passes this in as string, to reduce warnings parse it as a number later
        limit: {
            type: String,
            default: '0',
        },

        // contactsUsageAlert.jsp passes this in as string, to reduce warnings parse it as a number later
        used: {
            type: String,
            default: '0',
        },
        origin: {
            type: String,
            default: '',
        },
        hasManageBillingPermissionAsString: {
            type: String,
            default: 'false',
        },
    },

    data() {
        return {
            closed: false,
        };
    },

    mounted() {
        if (!this.isOverWarningLimit) {
            this.hideComponent();
        } else if (this.isOverLimit || this.isOverWarningLimit) {
            const amplitudeLog = {
                'Alert Type': this.isOverLimit ? 'Exceeded limit' : this.isOverWarningLimit ? 'Approaching limit' : null,
                Action: 'Viewed',
            };

            if (this.isMyAccountPage) {
                amplitudeLog['Event Source'] = 'My account page';
            } else if (this.isContactImportPage) {
                amplitudeLog['Event Source'] = 'Import contacts page';
            } else if (this.isAddContactPage) {
                amplitudeLog['Event Source'] = 'Add contact page';
            }

            amplitude.infusionsoft.track(amplitude.keap.events.CONTACT_LIMIT_ALERT, amplitudeLog);
        }
    },

    computed: {
        contactLimit() {
            return Number(this.limit);
        },

        contactUsed() {
            return Number(this.used);
        },

        bufferType() {
            if (this.isMyAccountPage) {
                return BUFFER_TYPE.PERCENTAGE;
            }

            return BUFFER_TYPE.STATIC;
        },

        bufferValue() {
            if (!this.isMyAccountPage) {
                return 5;
            }

            return 0.95;
        },

        isOverWarningLimit() {
            return this.contactUsed >= this.warningLimit;
        },

        isOverLimit() {
            return this.contactUsed >= this.contactLimit;
        },

        warningLimit() {
            switch (this.bufferType) {
            case BUFFER_TYPE.STATIC:
                return this.contactLimit - this.bufferValue;
            case BUFFER_TYPE.PERCENTAGE:
            default:
                return this.contactLimit * this.bufferValue;
            }
        },

        alertType() {
            return this.isOverLimit
                ? 'warning'
                : 'good';
        },

        isMyAccountPage() {
            return this.origin === CONTACT_USAGE_ALERT_LOCATION.MY_ACCOUNT;
        },

        isContactImportPage() {
            return this.origin === CONTACT_USAGE_ALERT_LOCATION.IMPORT_CONTACTS;
        },

        isAddContactPage() {
            return this.origin === CONTACT_USAGE_ALERT_LOCATION.ADD_CONTACT;
        },

        hasManageBillingPermission() {
            return this.hasManageBillingPermissionAsString === 'true';
        },
    },

    methods: {
        async closeComponent() {
            if (this.isMyAccountPage) {
                await axios.put('/app/contact/warning/my_account');
            } else if (this.isAddContactPage) {
                await axios.put('/app/contact/warning/add_contact');
            } else if (this.isContactImportPage) {
                await axios.put('/app/contact/warning/import_contacts');
            }

            this.hideComponent();
        },

        hideComponent() {
            window.parent.postMessage('hideContactUsageAlert', '*');
            this.closed = true;
        },

        openQuickAddModal() {
            const amplitudeLog = {
                'Alert Type': this.isOverLimit ? 'Exceeded limit' : this.isOverWarningLimit
                    ? 'Approaching limit' : null,
                Action: 'Clicked',
            };

            if (this.isContactImportPage) {
                amplitudeLog['Event Source'] = 'Import contacts page';
            } else if (this.isAddContactPage) {
                amplitudeLog['Event Source'] = 'Add contact page';
            } else if (this.isMyAccountPage) {
                amplitudeLog['Event Source'] = 'My account page';
            }

            amplitude.infusionsoft.track(amplitude.keap.events.CONTACT_LIMIT_ALERT, amplitudeLog);

            window.open('/Account/quickAdd.jsp?addonType=contact');
        },

        learnMore() {
            window.open('https://help.infusionsoft.com/help/limits-thresholds-and-overages', '_blank', 'noopener,noreferrer');
        },
    },
};
</script>

<style lang="scss" scoped>
.alert-title {
    font-weight: $font-weight-bold;
    font-size: $font-size-lg;
    color: $color-ink-900;
}

.usage-message {
    margin: $spacing-200 0;
    color: $color-ink-900;
    font-size: $font-size-sm;
    line-height: 20px;
}

.warning {
    --progress-bar-color: #{$color-orange-900};
}

.progress-bar {
    margin-bottom: $spacing-100;
}

.manage-account {
    width: 100%;
    display: flex;
    align-content: start;
    margin-top: 0;
}

</style>
